import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import axiosInstance from 'api/axiosInstance'
import {
  presignPhoto,
  uploadFileToS3
} from 'services/endpoints/attachment'
import { type ProgressValue } from 'types/iTypes'

type VerificationUploadKind = 'id_photo' | 'id_hold_photo'

type UploadSlot = ProgressValue & {
  uploadId: string
  s3Key?: string
}

const MAX_FILES = 3
const MAX_SIZE = 7340032
const VALID_FILE_TYPES = ['jpg', 'jpeg', 'png']

export const confirmVerificationPhotoUpload = async (
  payload: {
    s3_key: string
    kind: VerificationUploadKind
    original_name?: string
    mime_type?: string
    size?: number
  },
  signal?: AbortSignal
) => {
  try {
    return await axiosInstance.post(
      '/api/attachment/verification/photo/from-s3',
      payload,
      { signal }
    )
  } catch (error: any) {
    throw new Error(error?.message || 'Verification photo confirm failed')
  }
}

export function useIdVerificationUpload(formik: any) {
  const { t } = useTranslation()

  const [idProgressValues, setIdProgressValues] = useState<UploadSlot[]>([])
  const [idHoldProgressValues, setIdHoldProgressValues] = useState<UploadSlot[]>([])

  const idProgressRef = useRef<UploadSlot[]>([])
  const idHoldProgressRef = useRef<UploadSlot[]>([])

  const syncIdProgress = (next: UploadSlot[]) => {
    idProgressRef.current = next
    setIdProgressValues(next)
  }

  const syncIdHoldProgress = (next: UploadSlot[]) => {
    idHoldProgressRef.current = next
    setIdHoldProgressValues(next)
  }

  const setFieldError = (field: string, message: string) => {
    formik.setFieldError(field, message)
  }

  const clearFieldError = (field: string) => {
    formik.setFieldError(field, '')
  }

  const makeUploadId = (file: File) =>
    `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`

  const validateFiles = (files: FileList | null, errorField: string) => {
    if (!files || files.length === 0) return []

    if (files.length > MAX_FILES) {
      setFieldError(errorField, t('error:uploadMax3Photos'))
      return null
    }

    const parsed = Array.from(files)

    for (const file of parsed) {
      const ext = file.type.split('/')[1]?.toLowerCase()
      if (file.size > MAX_SIZE || !VALID_FILE_TYPES.includes(ext)) {
        setFieldError(errorField, t('error:invalidFileTypeOrSize'))
        return null
      }
    }

    clearFieldError(errorField)
    return parsed
  }

  const removeUploadById = (
    uploadId: string,
    type: 'id' | 'hold'
  ) => {
    if (type === 'id') {
      syncIdProgress(idProgressRef.current.filter(item => item.uploadId !== uploadId))
      return
    }

    syncIdHoldProgress(idHoldProgressRef.current.filter(item => item.uploadId !== uploadId))
  }

  const patchUploadProgress = (
    uploadId: string,
    patch: Partial<UploadSlot>,
    type: 'id' | 'hold'
  ) => {
    if (type === 'id') {
      syncIdProgress(
        idProgressRef.current.map(item =>
          item.uploadId === uploadId ? { ...item, ...patch } : item
        )
      )
      return
    }

    syncIdHoldProgress(
      idHoldProgressRef.current.map(item =>
        item.uploadId === uploadId ? { ...item, ...patch } : item
      )
    )
  }

  const appendUploads = (
    files: File[],
    type: 'id' | 'hold'
  ) => {
    const newItems: UploadSlot[] = files.map(file => ({
      uploadId: makeUploadId(file),
      name: file.name,
      percentage: 0,
      size: file.size,
      type: file.type.split('/')[1]
    }))

    if (type === 'id') {
      syncIdProgress([...idProgressRef.current, ...newItems])
    } else {
      syncIdHoldProgress([...idHoldProgressRef.current, ...newItems])
    }

    return newItems
  }

  const uploadOneFile = async (
    file: File,
    uploadId: string,
    kind: VerificationUploadKind,
    type: 'id' | 'hold'
  ) => {
    const presign = await presignPhoto({
    type: kind === 'id_photo' ? 'verification_id' : 'verification_id_hold',
    contentType: file.type,
    size: file.size
    })

    patchUploadProgress(uploadId, { s3Key: presign.key }, type)

    await uploadFileToS3(
      presign.url,
      file,
      (loaded, total, cancelCb) => {
        const percentage = Math.round((100 * loaded) / total)
        patchUploadProgress(
          uploadId,
          {
            percentage,
            cancel: cancelCb
          },
          type
        )
      }
    )

    const confirmRes = await confirmVerificationPhotoUpload({
      s3_key: presign.key,
      kind,
      original_name: file.name,
      mime_type: file.type,
      size: file.size
    })

    return {
      s3Key: presign.key,
      response: confirmRes
    }
  }

  const updateFormikPaths = (
    fieldName: 'idPhotoUrl' | 'idHoldPhotoUrl',
    confirmedItems: any[]
  ) => {
    const paths = confirmedItems
      .map(item => {
        const data = item?.response?.data?.data ?? item?.response?.data
        return data?.path ?? data?.url ?? data?.storage_path ?? data?.s3_key ?? null
      })
      .filter(Boolean)

    formik.setFieldValue(fieldName, paths)
  }

  const uploadFiles = async (
    files: File[],
    opts: {
      type: 'id' | 'hold'
      kind: VerificationUploadKind
      formikField: 'idPhotoUrl' | 'idHoldPhotoUrl'
      errorField: 'idPhotoUrl' | 'idHoldPhotoUrl'
    }
  ) => {
    const created = appendUploads(files, opts.type)

    try {
      const results = await Promise.all(
        files.map(async (file, index) =>
          await uploadOneFile(file, created[index].uploadId, opts.kind, opts.type).catch(error => {
            console.error(error)
            removeUploadById(created[index].uploadId, opts.type)
            return null
          })
        )
      )

      const confirmed = results.filter(Boolean)
      updateFormikPaths(opts.formikField, confirmed)
    } catch (error) {
      console.error(error)
      setFieldError(opts.errorField, t('error:uploadFailed'))
    }
  }

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = validateFiles(event.target.files, 'idPhotoUrl')
    if (!files) return

    await uploadFiles(files, {
      type: 'id',
      kind: 'id_photo',
      formikField: 'idPhotoUrl',
      errorField: 'idPhotoUrl'
    })

    event.target.value = ''
  }

  const handleHoldImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = validateFiles(event.target.files, 'idHoldPhotoUrl')
    if (!files) return

    await uploadFiles(files, {
      type: 'hold',
      kind: 'id_hold_photo',
      formikField: 'idHoldPhotoUrl',
      errorField: 'idHoldPhotoUrl'
    })

    event.target.value = ''
  }

  return {
    handleImageChange,
    handleHoldImageChange,
    idHoldProgressValues,
    idProgressValues
  }
}